import { GetExperiment } from '@carvana/core-experiment';
import { getUserState } from '@carvana/core-auth';
import Cookies from 'js-cookie';
import { datadogRum } from '@datadog/browser-rum';
import { setSarHighIntent } from '@store/sar-store';
import { getAppState, setAppState } from './State';
import { getOVPDataApi, getOVPMfeFeedDataApi } from '../services/OvpPageService';
import { ReferenceTypes, REFERENCE_TYPE_FALLBACK_ORDER, Experiments, AnalyticEvents } from '../constants';
import { checkOneYearHasPassed } from '../services/UtilitiesService';
import { VehicleMileageService } from '../services/vehicle-mileage-service';
import { isMyCarsReskinFixed } from '../utils/myCarsReskinExperiment';
import { resolveExperimentEnvironment } from '@utils/experimentTelemetry';
import { Analytics } from '../services/analytics-service';

const trackReskinEvent = (event: string, props: any) => {
  Analytics.trackEvent(event, props);
  datadogRum.addAction(event, props);
};

/* 
    Ovp Page Store -- is a wrapper around state and data access, 
    abstracts away data access logic out of the components. This 
    store is for owned vehicle page data access calls.

 */

// TODO: This gets messed up in local dev - why? Why are we saving state to session storage?

const environment = process.env.NODE_ENV;

const normalizeReferenceType = (referenceType: any): string => {
  const normalized = String(referenceType ?? '')
    .trim()
    .toLowerCase();
  return normalized === ReferenceTypes.VEHICLE || normalized === ReferenceTypes.APPRAISAL ? normalized : '';
};

// A known type yields a single attempt; an absent/unknown type tries vehicle then appraisal.
const getCandidateReferenceTypes = (referenceType: any): string[] => {
  const normalized = normalizeReferenceType(referenceType);
  return normalized ? [normalized] : [...REFERENCE_TYPE_FALLBACK_ORDER];
};

const filterOVPData = (state: any, vehicleId: number, referenceType: string) => {
  if (referenceType === ReferenceTypes.VEHICLE) {
    return state.ovpData.filter((ovpObj: any) => ovpObj.vehicle?.id === vehicleId);
  }
  return state.ovpData.filter((ovpObj: any) => ovpObj.appraisalId === vehicleId);
};

type HeaderFetchResult = { matched: any[]; effectiveType: string } | null;

const attemptOVPHeaderFetch = async (vehicleId: number, referenceType: string): Promise<HeaderFetchResult> => {
  let response = await getOVPDataApi(vehicleId, referenceType, environment);
  if (response?.data) {
    response = response.data;
  }
  if (!response || response.error) {
    return null;
  }
  const matched = filterOVPData({ ovpData: [response] }, vehicleId, referenceType);
  return matched.length > 0 ? { matched, effectiveType: referenceType } : null;
};

// Try each candidate type in order until one returns a matching row (sequential, short-circuiting).
const fetchFirstMatchingHeader = async (vehicleId: number, candidates: string[]): Promise<HeaderFetchResult> => {
  if (candidates.length === 0) {
    return null;
  }
  const [candidate, ...rest] = candidates;
  const attempt = await attemptOVPHeaderFetch(vehicleId, candidate);
  return attempt || fetchFirstMatchingHeader(vehicleId, rest);
};

// CONTROL (inventory-ltw-my-cars-reskin !== control_fixed): original single-reference-type fetch.
const getOVPHeaderDataControl = async (vehicleId: number): Promise<any> => {
  // check state for data
  const result: any = {};
  const state = getAppState();
  // return state if populated with data
  const { ovpReferenceType: referenceType } = state; // Appraisal ID/ Vehicle ID
  if (state && state?.ovpData?.length > 0) {
    const found = filterOVPData(state, vehicleId, referenceType);
    if (found.length > 0) {
      return found;
    }
  }
  // update state with and then return data
  let response = await getOVPDataApi(vehicleId, referenceType, environment);

  if (response.data) {
    response = response.data;
  }

  if (response !== null && response !== undefined) {
    state.ovpData.push(response);
    result.ovpData = state.ovpData;
  }
  if (response.error || response === null || response === undefined) {
    state.ovpDataError = true;
    result.ovpDataError = true;
  }
  if (result.ovpDataError) {
    return result;
  }
  const filteredData = filterOVPData(result, vehicleId, referenceType);
  if (referenceType === ReferenceTypes.VEHICLE) {
    state.isOneYearPurchase = checkOneYearHasPassed(filteredData[0].saleDate);
  }
  setAppState(state);

  return filteredData;
};

// CONTROL_FIXED (inventory-ltw-my-cars-reskin === control_fixed): try candidate reference types.
const getOVPHeaderDataFixed = async (vehicleId: number): Promise<any> => {
  const result: any = {};
  const state = getAppState();
  const candidates = getCandidateReferenceTypes(state?.ovpReferenceType);

  // return cached data if a candidate type already has a matching row
  if (state?.ovpData?.length > 0) {
    const cachedType = candidates.find((candidate) => filterOVPData(state, vehicleId, candidate).length > 0);
    if (cachedType) {
      setOvpReferenceType(cachedType);
      return filterOVPData(state, vehicleId, cachedType);
    }
  }

  const attempt = await fetchFirstMatchingHeader(vehicleId, candidates);
  if (!attempt) {
    state.ovpDataError = true;
    result.ovpDataError = true;
    return result;
  }

  const { matched, effectiveType } = attempt;
  trackReskinEvent(AnalyticEvents.myCarsReskinReferenceTypeResolved, {
    requestedReferenceType: normalizeReferenceType(state?.ovpReferenceType) || null,
    effectiveReferenceType: effectiveType,
    usedFallbackChain: candidates.length > 1,
    resolvedViaFallback: candidates.length > 1 && effectiveType !== candidates[0],
  });
  state.ovpData.push(matched[0]);
  setOvpReferenceType(effectiveType);
  if (effectiveType === ReferenceTypes.VEHICLE) {
    state.isOneYearPurchase = checkOneYearHasPassed(matched[0].saleDate);
  }
  setAppState(state);

  return matched;
};

export const getOVPHeaderData = async (vehicleId: number): Promise<any> =>
  isMyCarsReskinFixed() ? getOVPHeaderDataFixed(vehicleId) : getOVPHeaderDataControl(vehicleId);

export const getOvpMfeFeed = async (vehicleId: number): Promise<any> => {
  // check state for data
  const result: any = {};
  const state = getAppState();
  // update state with and then return data
  const { ovpReferenceType: referenceType } = state; // Appraisal ID/ Vehicle ID
  const bucket = await GetExperiment({
    name: Experiments.CORE_SERVICE_AND_REPAIRS,
    identifier: getUserState().user.user_id,
    environment: resolveExperimentEnvironment(),
  });
  const sarHighIntent = Cookies.get('Service-And-Repairs-High-Intent');
  let response = await getOVPMfeFeedDataApi(vehicleId, referenceType, environment, bucket, sarHighIntent);

  if (response.data) {
    response = response.data;
  }
  setSarHighIntent({
    isHighIntent: response?.serviceAndRepairsHighIntent,
    experiments: response?.experiments || {},
  });

  // SERV-405: Fetch vehicle mileage data from MyCars GraphQL API to calculate VCM-based fields
  // This enables "last known mileage" display and warranty mileage tracking
  if (response?.feed && referenceType === ReferenceTypes.VEHICLE) {
    const mileageData = await VehicleMileageService.fetchVehicleMileageData(vehicleId);

    if (mileageData) {
      // Augment feed cards with calculated mileage fields
      response.feed = response.feed.map((card: any) => {
        // Update VehicleCoverage card warranties
        if (card.type === 'VehicleCoverage' && card.content?.coverage) {
          return {
            ...card,
            content: {
              ...card.content,
              coverage: card.content.coverage.map((warranty: any) => ({
                ...warranty,
                purchaseMileage: mileageData.purchaseMileage,
                currentMileage: mileageData.currentMileage,
                mileageLastUpdated: mileageData.mileageLastUpdated,
              })),
            },
          };
        }

        // Update ServiceAndRepair card
        if (card.type === 'ServiceAndRepair' && card.content?.serviceAndRepair) {
          return {
            ...card,
            content: {
              ...card.content,
              serviceAndRepair: {
                ...card.content.serviceAndRepair,
                purchaseMileage: mileageData.purchaseMileage,
                // Use VCM current mileage when available, otherwise fall back to existing mileage
                mileage: mileageData.currentMileage ?? card.content.serviceAndRepair.mileage,
                mileageLastUpdated: mileageData.mileageLastUpdated,
              },
            },
          };
        }

        return card;
      });
    }
  }

  if (response?.feed) {
    state.ovpMfeData[vehicleId] = response.feed;
    result.ovpMfeData = state.ovpMfeData;
  }
  if (response?.error) {
    state.ovpMfeDataError = true;
    result.ovpMfeDataError = true;
  }
  setAppState(state);
  return response?.error || result?.ovpMfeData?.length === 0 ? result : result.ovpMfeData[vehicleId];
};

export const setOvpReferenceType = (referenceType: any) => {
  const state = getAppState();
  if (state) {
    state.ovpReferenceType = isMyCarsReskinFixed() ? normalizeReferenceType(referenceType) : referenceType;
    setAppState(state);
  }
};

export const getOvpReferenceType = (): string | undefined => {
  const state = getAppState();
  if (state) {
    return state.ovpReferenceType;
  }
};
